import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  Activity,
  BarChart3,
  Clock3,
  ExternalLink,
  FileText,
  Globe2,
  Info,
  LayoutDashboard,
  RefreshCw,
  Search
} from "lucide-react";
import { geoNaturalEarth1, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import countryLookup from "country-code-lookup";
import worldAtlas from "world-atlas/countries-110m.json";
import type {
  CountriesResponse,
  DimensionRow,
  PageRow,
  PagesResponse,
  RangeKey,
  RealtimeResponse,
  ReferrersResponse,
  StatsResponse,
  TimePoint
} from "./types";
import "./styles.css";

type CountryFeature = {
  type: "Feature";
  properties: {
    name?: string;
  };
  geometry: GeoJSON.Geometry;
};

type ViewKey = "overview" | "realtime" | "historical" | "pages" | "countries" | "referrers" | "about";

const ranges: { label: string; value: RangeKey }[] = [
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "7 days", value: "7d" },
  { label: "30 days", value: "30d" },
  { label: "90 days", value: "90d" },
  { label: "All time", value: "all" }
];

const nav = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "realtime", label: "Realtime", icon: Activity },
  { key: "historical", label: "Historical", icon: BarChart3 },
  { key: "pages", label: "Pages", icon: FileText },
  { key: "countries", label: "Countries", icon: Globe2 },
  { key: "referrers", label: "Referrers", icon: ExternalLink },
  { key: "about", label: "About", icon: Info }
] satisfies { key: ViewKey; label: string; icon: typeof LayoutDashboard }[];

function App() {
  const [view, setView] = useState<ViewKey>("overview");
  const [range, setRange] = useState<RangeKey>("7d");
  const stats = useApi<StatsResponse>(`/api/stats?range=${range}`, 30000);
  const realtime = useApi<RealtimeResponse>("/api/realtime", 10000);
  const pages = useApi<PagesResponse>(`/api/pages?range=${range}`, 30000);
  const countries = useApi<CountriesResponse>(`/api/countries?range=${range}`, 30000);
  const referrers = useApi<ReferrersResponse>(`/api/referrers?range=${range}`, 30000);

  const generatedAt = stats.data?.generatedAt ?? realtime.data?.generatedAt;

  return (
    <div className="min-h-screen bg-pitch text-slate-100">
      <div className="fixed inset-0 -z-10 bg-[radial-gradient(circle_at_25%_0%,rgba(39,209,127,0.13),transparent_34%),linear-gradient(135deg,rgba(246,215,91,0.08),transparent_30%),linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(0deg,rgba(255,255,255,0.035)_1px,transparent_1px)] bg-[length:auto,auto,72px_72px,72px_72px]" />
      <aside className="fixed inset-y-0 left-0 hidden w-64 border-r border-white/10 bg-black/20 p-5 backdrop-blur xl:block">
        <Brand />
        <nav className="mt-8 space-y-1">
          {nav.map((item) => (
            <NavButton key={item.key} item={item} active={view === item.key} onClick={() => setView(item.key)} />
          ))}
        </nav>
      </aside>

      <main className="xl:pl-64">
        <header className="sticky top-0 z-20 border-b border-white/10 bg-pitch/80 backdrop-blur">
          <div className="flex flex-col gap-4 px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="xl:hidden">
                <Brand />
              </div>
              <div className="hidden xl:block">
                <p className="text-sm text-slate-400">wc.evolve.tt</p>
                <h1 className="text-2xl font-semibold tracking-normal">Visitor Analytics</h1>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Clock3 className="h-4 w-4 text-grass" />
                <span>{generatedAt ? `Updated ${formatTime(generatedAt)}` : "Waiting for data"}</span>
                <button
                  className="icon-button"
                  title="Refresh analytics"
                  onClick={() => {
                    stats.reload();
                    realtime.reload();
                    pages.reload();
                    countries.reload();
                    referrers.reload();
                  }}
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex gap-2 overflow-x-auto xl:hidden">
              {nav.map((item) => (
                <button
                  key={item.key}
                  className={`mobile-tab ${view === item.key ? "mobile-tab-active" : ""}`}
                  onClick={() => setView(item.key)}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </header>

        <div className="px-4 py-6 sm:px-6 lg:px-8">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium uppercase tracking-[0.22em] text-grass">WC Visitors</p>
              <h2 className="mt-1 text-3xl font-semibold tracking-normal">{nav.find((item) => item.key === view)?.label}</h2>
            </div>
            {view !== "realtime" && view !== "about" && <RangePicker range={range} setRange={setRange} />}
          </div>

          <Status error={stats.error || realtime.error || pages.error || countries.error || referrers.error} />

          {view === "overview" && <Overview stats={stats.data} realtime={realtime.data} loading={stats.loading} />}
          {view === "realtime" && <RealtimePanel realtime={realtime.data} loading={realtime.loading} />}
          {view === "historical" && <Historical stats={stats.data} />}
          {view === "pages" && <PagesTable rows={pages.data?.pages ?? []} />}
          {view === "countries" && <Geography rows={countries.data?.countries ?? []} />}
          {view === "referrers" && <DimensionTable title="Referrer statistics" rows={referrers.data?.referrers ?? []} />}
          {view === "about" && <About />}
        </div>
      </main>
    </div>
  );
}

function About() {
  const overview = [
    "Privacy-friendly visitor analytics for the WC 2026 Live Tracker.",
    "Runs entirely on Cloudflare Pages, Pages Functions, Workers, and Workers Analytics Engine.",
    "Tracks pageviews, anonymous session estimates, countries, referrers, top pages, and near-realtime activity.",
    "Avoids cookies, raw IP storage, full user agents, third-party analytics scripts, and user login for v1."
  ];

  const improvements = [
    "Replaced ad hoc dashboard guessing with live Analytics Engine ingestion and SQL-backed aggregates.",
    "Added production diagnostics for Analytics Engine bindings, environment variables, dataset visibility, and sample counts.",
    "Added a local SVG world map for country-level analytics without external map tiles.",
    "Hardened Cloudflare deployment by opting the Vite module bundle out of Rocket Loader."
  ];

  const futureFeatures = [
    "Hourly chart buckets once a Cloudflare-compatible time-bucketing expression is finalized.",
    "Bot filtering and internal traffic exclusion rules.",
    "CSV export for pages, countries, and referrers.",
    "Passwordless admin protection or Cloudflare Access for non-public dashboard deployments.",
    "Campaign/source tagging and richer page title reporting."
  ];

  const limitations = [
    "Unique visitors are estimates based on salted anonymous session hashes.",
    "Analytics Engine can have short ingestion/query availability delays.",
    "Realtime activity is approximate and depends on Analytics Engine query freshness.",
    "Country data comes from Cloudflare request metadata and may be absent for some edge cases.",
    "No individual user journey reconstruction by design."
  ];

  const architecture = [
    "wc.evolve.tt loads a tiny async tracker from wc-visitors.evolve.tt/tracker.js.",
    "The tracker sends pageviews to POST /api/track using sendBeacon with fetch fallback.",
    "A Cloudflare Pages Function validates and normalizes events, then writes to Workers Analytics Engine.",
    "Dashboard API routes query Analytics Engine through Cloudflare's SQL API.",
    "The React dashboard polls API routes for overview, realtime, historical, page, country, and referrer views."
  ];

  const stack = [
    "Cloudflare Pages",
    "Cloudflare Pages Functions",
    "Cloudflare Workers Analytics Engine",
    "Cloudflare Analytics Engine SQL API",
    "React 18",
    "Vite",
    "TypeScript",
    "Tailwind CSS",
    "d3-geo, topojson-client, world-atlas"
  ];

  const schema = [
    "`index1`: salted anonymous session hash",
    "`blob1`: page path",
    "`blob2`: referrer hostname",
    "`blob3`: country",
    "`blob4`: continent",
    "`blob5`: Cloudflare colo",
    "`blob6`: browser family",
    "`blob7`: operating system family",
    "`blob8`: device category",
    "`blob9`: session hash copy",
    "`blob10`: page title",
    "`blob11`: event type",
    "`blob12`: source host",
    "`blob13`: schema version",
    "`double1`-`double5`: response/load metrics and client timestamp"
  ];

  const dataSources = [
    "Client page path, title, referrer, and browser performance navigation metrics.",
    "Cloudflare request metadata for country, continent, and colo.",
    "Reduced user-agent families for browser, operating system, and device category.",
    "No raw IP addresses, cookies, names, emails, or fingerprinting identifiers."
  ];

  const routes = [
    "`POST /api/track`: validate and ingest pageviews",
    "`GET /api/stats`: overview totals, trends, countries, pages, and referrers",
    "`GET /api/realtime`: active visitors and recent activity",
    "`GET /api/pages`: top pages",
    "`GET /api/countries`: country statistics",
    "`GET /api/referrers`: referrer statistics",
    "`GET /api/diagnostics`: safe deployment and Analytics Engine checks",
    "`GET /tracker.js`: lightweight source-site tracker"
  ];

  const keyFiles = [
    "`functions/[[path]].ts`: Pages Function, tracker, ingestion, SQL queries, API routes",
    "`src/main.tsx`: React dashboard, navigation, charts, world map, tables",
    "`src/styles.css`: Tailwind component styles and map states",
    "`src/types.ts`: shared API response types",
    "`wrangler.toml`: Pages output, environment variables, Analytics Engine binding",
    "`vite.config.ts`: React build and Cloudflare Rocket Loader opt-out transform",
    "`README.md`: deployment and operations guide"
  ];

  return (
    <div className="grid gap-5 xl:grid-cols-2">
      <section className="rounded-lg border border-grass/20 bg-[rgba(11,23,20,0.82)] p-6 shadow-glow">
        <p className="text-sm font-medium uppercase tracking-[0.22em] text-grass">Head Info</p>
        <div className="mt-4 flex flex-col gap-3">
          <a className="about-title-link" href="https://wc.evolve.tt/" target="_blank" rel="noreferrer">
            WC 2026 Live Tracker
            <ExternalLink className="h-4 w-4" />
          </a>
          <p className="text-slate-300">Claude · Codex · Grok</p>
          <p className="text-slate-400">w / Dale</p>
          <div className="flex flex-wrap gap-2">
            <AboutLink href="https://evolve.tt/">evolve.tt</AboutLink>
            <AboutLink href="https://dale.chulhan.com/">dale.chulhan.com</AboutLink>
            <AboutLink href="https://dale.devs.tt/">dale.devs.tt</AboutLink>
          </div>
        </div>
      </section>

      <AboutList title="Overview of this web app" items={overview} />
      <AboutList title="Improvements" items={improvements} />
      <AboutList title="Future features" items={futureFeatures} />
      <AboutList title="Limitations" items={limitations} />
      <AboutList title="Architecture" items={architecture} />
      <AboutList title="Tech stack" items={stack} />
      <AboutList title="Database schema" items={schema} />
      <AboutList title="Data sources" items={dataSources} />
      <AboutList title="Worker API routes" items={routes} />
      <AboutList title="Key files" items={keyFiles} />
    </div>
  );
}

function AboutLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a className="about-link" href={href} target="_blank" rel="noreferrer">
      {children}
      <ExternalLink className="h-3.5 w-3.5" />
    </a>
  );
}

function AboutList({ title, items }: { title: string; items: string[] }) {
  return (
    <Panel title={title}>
      <ul className="space-y-3 text-sm leading-6 text-slate-300">
        {items.map((item) => (
          <li key={item} className="flex gap-3">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-grass" />
            <span>{renderInlineCode(item)}</span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function Geography({ rows }: { rows: DimensionRow[] }) {
  return (
    <div className="space-y-5">
      <Panel title="World map">
        <WorldMap rows={rows} />
      </Panel>
      <DimensionTable title="Country statistics" rows={rows} />
    </div>
  );
}

function WorldMap({ rows }: { rows: DimensionRow[] }) {
  const { features, paths, byCountry, max } = useMemo(() => buildWorldMap(rows), [rows]);

  return (
    <div className="grid gap-5 xl:grid-cols-[1.35fr_0.65fr]">
      <div className="overflow-hidden rounded-lg border border-white/10 bg-black/20">
        <svg viewBox="0 0 960 500" className="block h-auto w-full" role="img" aria-label="World map of visitors by country">
          <rect width="960" height="500" fill="rgba(255,255,255,0.025)" />
          <path d="M0 250 H960" stroke="rgba(255,255,255,0.05)" />
          <path d="M480 0 V500" stroke="rgba(255,255,255,0.05)" />
          {features.map((country, index) => {
            const name = country.properties.name ?? "Unknown";
            const iso2 = countryNameToIso2(name);
            const row = iso2 ? byCountry.get(iso2) : undefined;
            const intensity = row ? Math.max(0.18, row.pageviews / max) : 0;
            return (
              <path
                key={`${name}-${index}`}
                d={paths[index] ?? ""}
                className="map-country"
                fill={row ? colorForIntensity(intensity) : "rgba(148,163,184,0.16)"}
                stroke={row ? "rgba(246,215,91,0.42)" : "rgba(255,255,255,0.08)"}
                strokeWidth={row ? 0.75 : 0.45}
              >
                <title>
                  {name}
                  {row ? `: ${formatNumber(row.pageviews)} pageviews, ${formatNumber(row.visitors)} visitors` : ": no visits"}
                </title>
              </path>
            );
          })}
        </svg>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <MetricCard compact label="Countries" value={rows.length} />
          <MetricCard compact label="Top country views" value={max === 1 && rows.length === 0 ? 0 : max} />
        </div>
        <BarList rows={rows.slice(0, 8)} />
      </div>
    </div>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-3">
      <div className="grid h-10 w-10 place-items-center rounded border border-grass/40 bg-grass/10 shadow-glow">
        <BarChart3 className="h-5 w-5 text-grass" />
      </div>
      <div>
        <p className="font-semibold">WC Visitors</p>
        <p className="text-xs text-slate-400">Cloudflare analytics</p>
      </div>
    </div>
  );
}

function NavButton({
  item,
  active,
  onClick
}: {
  item: (typeof nav)[number];
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button className={`nav-button ${active ? "nav-button-active" : ""}`} onClick={onClick}>
      <item.icon className="h-4 w-4" />
      <span>{item.label}</span>
    </button>
  );
}

function RangePicker({ range, setRange }: { range: RangeKey; setRange: (range: RangeKey) => void }) {
  return (
    <div className="segmented" aria-label="Date range">
      {ranges.map((item) => (
        <button key={item.value} className={range === item.value ? "active" : ""} onClick={() => setRange(item.value)}>
          {item.label}
        </button>
      ))}
    </div>
  );
}

function Overview({
  stats,
  realtime,
  loading
}: {
  stats?: StatsResponse;
  realtime?: RealtimeResponse;
  loading: boolean;
}) {
  const totals = stats?.totals;
  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Visitors today" value={totals?.visitorsToday} compare={totals ? delta(totals.visitorsToday, totals.visitorsYesterday) : undefined} />
        <MetricCard label="Pageviews today" value={totals?.pageviewsToday} compare={totals ? delta(totals.pageviewsToday, totals.pageviewsYesterday) : undefined} />
        <MetricCard label="Unique visitors" value={totals?.uniqueVisitors} />
        <MetricCard label="Pages per visit" value={totals?.averagePagesPerVisit} decimals={2} />
      </div>
      <div className="grid gap-5 xl:grid-cols-[1.4fr_1fr]">
        <Panel title="Pageviews and visitors">
          <AreaChart points={stats?.series ?? []} loading={loading} />
        </Panel>
        <Panel title="Now">
          <div className="grid grid-cols-2 gap-3">
            <MetricCard compact label="Active 5m" value={realtime?.active5m} />
            <MetricCard compact label="Active 30m" value={realtime?.active30m} />
          </div>
          <MiniList rows={realtime?.activeCountries ?? []} />
        </Panel>
      </div>
      <div className="grid gap-5 xl:grid-cols-3">
        <Panel title="Top countries">
          <MiniList rows={stats?.topCountries ?? []} />
        </Panel>
        <Panel title="Top pages">
          <MiniList rows={stats?.topPages ?? []} />
        </Panel>
        <Panel title="Top referrers">
          <MiniList rows={stats?.topReferrers ?? []} />
        </Panel>
      </div>
    </div>
  );
}

function RealtimePanel({ realtime, loading }: { realtime?: RealtimeResponse; loading: boolean }) {
  return (
    <div className="grid gap-5 xl:grid-cols-[0.8fr_1.2fr]">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <MetricCard label="Active 5m" value={realtime?.active5m} />
          <MetricCard label="Active 30m" value={realtime?.active30m} />
        </div>
        <Panel title="Countries currently active">
          <MiniList rows={realtime?.activeCountries ?? []} />
        </Panel>
      </div>
      <Panel title="Latest events">
        {loading && !realtime ? <SkeletonRows /> : null}
        <div className="space-y-2">
          {(realtime?.recentEvents ?? []).map((event, index) => (
            <div key={`${event.timestamp}-${index}`} className="event-row">
              <div>
                <p className="font-medium text-slate-100">{event.path}</p>
                <p className="text-xs text-slate-400">{event.title}</p>
              </div>
              <div className="text-right text-xs text-slate-400">
                <p>
                  {event.country} · {event.browser} · {event.device}
                </p>
                <p>{formatTime(event.timestamp)}</p>
              </div>
            </div>
          ))}
          {!loading && realtime?.recentEvents.length === 0 ? <EmptyState /> : null}
        </div>
      </Panel>
    </div>
  );
}

function Historical({ stats }: { stats?: StatsResponse }) {
  return (
    <div className="space-y-5">
      <Panel title="Traffic trend">
        <AreaChart points={stats?.series ?? []} />
      </Panel>
      <div className="grid gap-5 xl:grid-cols-3">
        <Panel title="Countries">
          <BarList rows={stats?.topCountries ?? []} />
        </Panel>
        <Panel title="Referrers">
          <BarList rows={stats?.topReferrers ?? []} />
        </Panel>
        <Panel title="Pages">
          <BarList rows={stats?.topPages ?? []} />
        </Panel>
      </div>
    </div>
  );
}

function PagesTable({ rows }: { rows: PageRow[] }) {
  return (
    <Panel title="Top pages">
      <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Path</th>
              <th>Pageviews</th>
              <th>Visitors</th>
              <th>Trend</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.path}>
                <td className="font-medium text-slate-100">{row.path}</td>
                <td>{formatNumber(row.pageviews)}</td>
                <td>{formatNumber(row.visitors)}</td>
                <td className={row.trend >= 0 ? "text-grass" : "text-ruby"}>{row.trend >= 0 ? "+" : ""}{formatNumber(row.trend)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 ? <EmptyState /> : null}
      </div>
    </Panel>
  );
}

function DimensionTable({ title, rows }: { title: string; rows: DimensionRow[] }) {
  return (
    <Panel title={title}>
      <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Pageviews</th>
              <th>Visitors</th>
              <th>Share</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.name}>
                <td className="font-medium text-slate-100">{row.name}</td>
                <td>{formatNumber(row.pageviews)}</td>
                <td>{formatNumber(row.visitors)}</td>
                <td>
                  <InlineBar value={row.pageviews} max={Math.max(...rows.map((item) => item.pageviews), 1)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 ? <EmptyState /> : null}
      </div>
    </Panel>
  );
}

function MetricCard({
  label,
  value,
  compare,
  decimals = 0,
  compact = false
}: {
  label: string;
  value?: number;
  compare?: number;
  decimals?: number;
  compact?: boolean;
}) {
  return (
    <div className={`metric-card ${compact ? "p-4" : "p-5"}`}>
      <p className="text-sm text-slate-400">{label}</p>
      <div className="mt-2 flex items-end justify-between gap-3">
        <p className="text-3xl font-semibold tracking-normal">{value === undefined ? "0" : formatNumber(value, decimals)}</p>
        {compare !== undefined ? (
          <span className={`rounded border px-2 py-1 text-xs ${compare >= 0 ? "border-grass/30 bg-grass/10 text-grass" : "border-ruby/30 bg-ruby/10 text-ruby"}`}>
            {compare >= 0 ? "+" : ""}
            {compare}%
          </span>
        ) : null}
      </div>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-white/10 bg-ink/78 p-5 shadow-glow">
      <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.18em] text-slate-400">{title}</h3>
      {children}
    </section>
  );
}

function MiniList({ rows }: { rows: DimensionRow[] }) {
  return (
    <div className="space-y-3">
      {rows.slice(0, 8).map((row) => (
        <div key={row.name} className="flex items-center justify-between gap-3">
          <span className="truncate text-sm text-slate-200">{row.name}</span>
          <span className="text-sm font-medium text-slate-100">{formatNumber(row.pageviews)}</span>
        </div>
      ))}
      {rows.length === 0 ? <EmptyState compact /> : null}
    </div>
  );
}

function BarList({ rows }: { rows: DimensionRow[] }) {
  const max = Math.max(...rows.map((row) => row.pageviews), 1);
  return (
    <div className="space-y-4">
      {rows.map((row) => (
        <div key={row.name}>
          <div className="mb-2 flex items-center justify-between gap-3 text-sm">
            <span className="truncate text-slate-200">{row.name}</span>
            <span className="font-medium">{formatNumber(row.pageviews)}</span>
          </div>
          <InlineBar value={row.pageviews} max={max} />
        </div>
      ))}
      {rows.length === 0 ? <EmptyState compact /> : null}
    </div>
  );
}

function InlineBar({ value, max }: { value: number; max: number }) {
  return (
    <div className="h-2 w-full rounded bg-white/8">
      <div className="h-2 rounded bg-gradient-to-r from-grass to-gold" style={{ width: `${Math.max(4, (value / max) * 100)}%` }} />
    </div>
  );
}

function AreaChart({ points, loading = false }: { points: TimePoint[]; loading?: boolean }) {
  const chart = useMemo(() => buildChart(points), [points]);
  if (loading && points.length === 0) return <div className="h-72 animate-pulse rounded bg-white/5" />;
  if (points.length === 0) return <EmptyState />;

  return (
    <div className="h-72 w-full">
      <svg viewBox="0 0 800 280" className="h-full w-full overflow-visible" role="img" aria-label="Traffic chart">
        <defs>
          <linearGradient id="visitorsLine" x1="0" x2="1" y1="0" y2="0">
            <stop stopColor="#27d17f" />
            <stop offset="1" stopColor="#f6d75b" />
          </linearGradient>
        </defs>
        <path d={chart.pageviewPath} fill="none" stroke="rgba(246,215,91,0.72)" strokeWidth="3" />
        <path d={chart.visitorPath} fill="none" stroke="url(#visitorsLine)" strokeWidth="3" />
        {chart.pageviewPoints.map((point, index) => (
          <circle key={`pageviews-${index}`} cx={point.x} cy={point.y} r="5" fill="#f6d75b" stroke="#07120f" strokeWidth="2" />
        ))}
        {chart.visitorPoints.map((point, index) => (
          <circle key={`visitors-${index}`} cx={point.x} cy={point.y} r="5" fill="#27d17f" stroke="#07120f" strokeWidth="2" />
        ))}
        {chart.grid.map((y) => (
          <line key={y} x1="0" x2="800" y1={y} y2={y} stroke="rgba(255,255,255,0.08)" />
        ))}
      </svg>
      <div className="mt-2 flex items-center gap-5 text-xs text-slate-400">
        <span className="legend bg-gold" /> Pageviews
        <span className="legend bg-grass" /> Visitors
      </div>
    </div>
  );
}

function buildChart(points: TimePoint[]) {
  const width = 800;
  const height = 260;
  const max = Math.max(...points.flatMap((point) => [point.pageviews, point.visitors]), 1);
  const x = (index: number) => (points.length === 1 ? width / 2 : (index / (points.length - 1)) * width);
  const y = (value: number) => height - (value / max) * (height - 24) + 8;
  const path = (key: "pageviews" | "visitors") =>
    points.map((point, index) => `${index === 0 ? "M" : "L"} ${x(index).toFixed(1)} ${y(point[key]).toFixed(1)}`).join(" ");
  const pointList = (key: "pageviews" | "visitors") =>
    points.map((point, index) => ({
      x: Number(x(index).toFixed(1)),
      y: Number(y(point[key]).toFixed(1))
    }));
  return {
    pageviewPath: path("pageviews"),
    visitorPath: path("visitors"),
    pageviewPoints: pointList("pageviews"),
    visitorPoints: pointList("visitors"),
    grid: [32, 96, 160, 224]
  };
}

function Status({ error }: { error?: string }) {
  if (!error) return null;
  return <div className="mb-5 rounded border border-ruby/40 bg-ruby/10 px-4 py-3 text-sm text-ruby">{error}</div>;
}

function EmptyState({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`grid place-items-center rounded border border-dashed border-white/10 bg-white/[0.03] text-center text-sm text-slate-500 ${compact ? "h-24" : "h-52"}`}>
      <div>
        <Search className="mx-auto mb-2 h-5 w-5" />
        No analytics yet
      </div>
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 6 }, (_, index) => (
        <div key={index} className="h-16 animate-pulse rounded bg-white/5" />
      ))}
    </div>
  );
}

function useApi<T>(path: string, interval: number) {
  const [data, setData] = useState<T>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let ignore = false;

    async function load() {
      try {
        setError(undefined);
        const response = await fetch(path, { headers: { accept: "application/json" } });
        if (!response.ok) throw new Error(`Request failed: ${response.status}`);
        const next = (await response.json()) as T;
        if (!ignore) setData(next);
      } catch (err) {
        if (!ignore) setError(err instanceof Error ? err.message : "Request failed");
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    load();
    const id = window.setInterval(load, interval);
    return () => {
      ignore = true;
      window.clearInterval(id);
    };
  }, [path, interval, tick]);

  return { data, loading, error, reload: () => setTick((value) => value + 1) };
}

function delta(current: number, previous: number) {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
}

function formatNumber(value: number, decimals = 0) {
  return new Intl.NumberFormat("en", { maximumFractionDigits: decimals, minimumFractionDigits: decimals }).format(value);
}

function formatTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en", { hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(date);
}

function renderInlineCode(text: string) {
  const parts = text.split(/(`[^`]+`)/g);
  return parts.map((part, index) =>
    part.startsWith("`") && part.endsWith("`") ? (
      <code key={`${part}-${index}`} className="rounded border border-white/10 bg-black/25 px-1.5 py-0.5 text-grass">
        {part.slice(1, -1)}
      </code>
    ) : (
      <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
    )
  );
}

function buildWorldMap(rows: DimensionRow[]) {
  const collection = feature(
    worldAtlas as unknown as Parameters<typeof feature>[0],
    (worldAtlas as { objects: { countries: unknown } }).objects.countries as Parameters<typeof feature>[1]
  ) as unknown as { features: CountryFeature[] };
  const projection = geoNaturalEarth1().fitSize([940, 470], collection as unknown as GeoJSON.FeatureCollection).translate([480, 252]);
  const path = geoPath(projection);
  const byCountry = new Map(rows.map((row) => [row.name.toUpperCase(), row]));
  const max = Math.max(...rows.map((row) => row.pageviews), 1);

  return {
    features: collection.features,
    paths: collection.features.map((country) => path(country as unknown as GeoJSON.Feature) ?? ""),
    byCountry,
    max
  };
}

function countryNameToIso2(name: string) {
  const alias: Record<string, string> = {
    "Antarctica": "AQ",
    "Bolivia": "BO",
    "Brunei": "BN",
    "Czechia": "CZ",
    "Dem. Rep. Congo": "CD",
    "Falkland Is.": "FK",
    "Iran": "IR",
    "Kosovo": "XK",
    "Laos": "LA",
    "Moldova": "MD",
    "North Korea": "KP",
    "Palestine": "PS",
    "Russia": "RU",
    "Somaliland": "SO",
    "South Korea": "KR",
    "Syria": "SY",
    "Taiwan": "TW",
    "Tanzania": "TZ",
    "United Kingdom": "GB",
    "United States of America": "US",
    "Venezuela": "VE",
    "Vietnam": "VN",
    "W. Sahara": "EH"
  };

  return alias[name] ?? countryLookup.byCountry(name)?.iso2 ?? null;
}

function colorForIntensity(value: number) {
  if (value > 0.72) return "#f6d75b";
  if (value > 0.44) return "#6ee79e";
  if (value > 0.22) return "#27d17f";
  return "#1f8f60";
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
